import {
  CrossOutline,
  DatePickerInput,
  DropdownInput,
  Individual,
  ExclamationHexagonBold as ExclamationHexagon,
} from '@aslanpay/portal-common-lib';
import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Radio,
  RadioGroup,
  Spacer,
  Stack,
  Text,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';

import PhoneNumberInput from 'app/components/UI/Inputs/PhoneNumberInput';
import useAuthContext from 'app/hooks/useAuthContext';
import useFetchPayCalendars from 'app/hooks/useFetchPayCalendars';
import personFormSchema, { personFormSchemaDefault } from 'app/utils/schemas/personForm';
import {
  Controller,
  useForm,
  Control,
  FieldErrors,
  UseFormSetValue,
  Resolver,
} from 'react-hook-form';

import { PersonFormFields, PersonFormProps } from './PersonForm.types';

function renderAdminFields(
  watchCanTopUpCompanyPoints: boolean | undefined,
  user: Individual | undefined,
  setValue: UseFormSetValue<PersonFormFields>,
  isEditMode: boolean,
  control: Control<PersonFormFields>,
  errors: FieldErrors<PersonFormFields>,
  isOnlyAdminWithPointsPermission?: boolean
) {
  const userCanTopUp = user?.canTopUpCompanyPoints ?? false;
  const isOnlyAdminWithPermission =
    isEditMode && isOnlyAdminWithPointsPermission && watchCanTopUpCompanyPoints;
  const isCanTopUpCompanyPointsDisabled = !userCanTopUp || isOnlyAdminWithPermission;

  return (
    <>
      {isEditMode && (
        <FormControl isInvalid={!!errors?.phoneNumber?.message}>
          <FormLabel>Mobile number (optional)</FormLabel>
          <Controller
            control={control}
            name="phoneNumber"
            render={({ field: { onChange, onBlur, value } }) => (
              <PhoneNumberInput
                defaultValue={value}
                placeholder="Enter their mobile number"
                onChange={onChange}
                onBlur={onBlur}
              />
            )}
          />
          <FormErrorMessage>{errors?.phoneNumber?.message}</FormErrorMessage>
        </FormControl>
      )}

      <FormControl isRequired>
        <FormLabel>Give this admin permission to buy points?</FormLabel>
        <RadioGroup
          mt="14px"
          value={watchCanTopUpCompanyPoints ? 'yes' : 'no'}
          isDisabled={isCanTopUpCompanyPointsDisabled}
          onChange={(value) =>
            setValue('canTopUpCompanyPoints', value === 'yes', {
              shouldValidate: true,
              shouldDirty: true,
            })
          }
        >
          <Stack spacing="10px">
            <Radio value="yes">Yes</Radio>
            <Radio value="no">No</Radio>
          </Stack>
          {isCanTopUpCompanyPointsDisabled && (
            <Flex flexDir="row" align="center" mt="14px" gap="12px">
              <ExclamationHexagon color="system.warning" boxSize="28px" />
              <Text textStyle="smallMedium" color="brand.black">
                {isOnlyAdminWithPermission
                  ? 'You cannot remove this as they are the only admin with this permission'
                  : 'You do not have permission to change this'}
              </Text>
            </Flex>
          )}
        </RadioGroup>
      </FormControl>
    </>
  );
}

function getSalariedValue(watchSalaried: boolean | undefined): string {
  if (watchSalaried === undefined) return '';
  return watchSalaried ? 'salaried' : 'non-salaried';
}

function getSubmitButtonText(isEditMode: boolean, isAdminRole: boolean) {
  if (isEditMode) return isAdminRole ? 'Save changes' : 'Save';
  return isAdminRole ? 'Create admin' : 'Add person';
}

function PersonForm({
  onSubmit,
  onCancel,
  mode,
  individualRole = 'EMPLOYEE',
  initialValues,
  isLoading = false,
  isOnlyAdminWithPointsPermission,
  isGiftingOnly = false,
}: PersonFormProps) {
  const { user, company } = useAuthContext();
  const isEditMode = mode === 'edit';
  const isAdminRole = individualRole === 'COMPANY_ADMIN';

  const { data: payCalendars } = useFetchPayCalendars({
    companyId: !isGiftingOnly && !isAdminRole ? [company?.id || ''] : [],
    page: 0,
    size: 999999,
  });

  const payCalendarOptions =
    payCalendars?.list.map((pc) => ({ value: pc.id, text: pc.name })) ?? [];

  const schema = isGiftingOnly ? personFormSchema : personFormSchemaDefault;

  const {
    register,
    control,
    handleSubmit,
    watch,
    resetField,
    setFocus,
    setValue,
    formState: { errors, isValid, isSubmitting, isDirty },
  } = useForm<PersonFormFields>({
    mode: 'onChange',
    defaultValues: {
      firstName: initialValues?.firstName || '',
      lastName: initialValues?.lastName || '',
      email: initialValues?.email || '',
      startDate: initialValues?.startDate || undefined,
      phoneNumber: initialValues?.phoneNumber || undefined,
      canTopUpCompanyPoints: initialValues?.canTopUpCompanyPoints || false,
      externalPayrollId: initialValues?.externalPayrollId || '',
      payCalendarId: initialValues?.payCalendarId || undefined,
      salaried: initialValues?.salaried ?? undefined,
    },
    resolver: yupResolver(schema) as Resolver<PersonFormFields>,
  });

  const watchFirstName = watch('firstName');
  const watchLastName = watch('lastName');
  const watchEmail = watch('email');
  const watchCanTopUpCompanyPoints = watch('canTopUpCompanyPoints');
  const watchSalaried = watch('salaried');

  const handleFormSubmit = (data: PersonFormFields) => {
    onSubmit(data);
  };

  const resetAndFocusField = (field: keyof PersonFormFields) => {
    resetField(field, { defaultValue: '' });
    setFocus(field);
  };

  const isStartDateDisabled = isEditMode;
  const submitButtonText = getSubmitButtonText(isEditMode, isAdminRole);
  const isSubmitDisabled = isEditMode ? !isValid || !isDirty : !isValid;

  return (
    <Flex flexDir="column" gap="10px">
      <form id="person-form" onSubmit={handleSubmit(handleFormSubmit)} noValidate>
        <Stack spacing="20px">
          <HStack spacing="20px" alignItems="flex-start">
            <FormControl isInvalid={!!errors?.firstName?.message} isRequired>
              <FormLabel>First name</FormLabel>
              <InputGroup>
                <Input
                  type="text"
                  placeholder="Enter their first name"
                  {...register('firstName')}
                />
                {watchFirstName && (
                  <InputRightElement>
                    <IconButton
                      aria-label="Clear first name"
                      icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                      variant="link"
                      onClick={() => resetAndFocusField('firstName')}
                    />
                  </InputRightElement>
                )}
              </InputGroup>
              <FormErrorMessage>{errors?.firstName?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors?.lastName?.message} isRequired>
              <FormLabel>Last name</FormLabel>
              <InputGroup>
                <Input type="text" placeholder="Enter their last name" {...register('lastName')} />
                {watchLastName && (
                  <InputRightElement>
                    <IconButton
                      aria-label="Clear last name"
                      icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                      variant="link"
                      onClick={() => resetAndFocusField('lastName')}
                    />
                  </InputRightElement>
                )}
              </InputGroup>
              <FormErrorMessage>{errors?.lastName?.message}</FormErrorMessage>
            </FormControl>
          </HStack>

          <FormControl isInvalid={!!errors?.email?.message} isRequired>
            <FormLabel>Company email</FormLabel>
            <InputGroup>
              <Input type="text" placeholder="Enter their email" {...register('email')} />
              {watchEmail && (
                <InputRightElement>
                  <IconButton
                    aria-label="Clear email"
                    icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                    variant="link"
                    onClick={() => resetAndFocusField('email')}
                  />
                </InputRightElement>
              )}
            </InputGroup>
            <FormErrorMessage>{errors?.email?.message}</FormErrorMessage>
          </FormControl>

          {!isAdminRole && (
            <>
              <FormControl
                isInvalid={!!errors?.startDate?.message}
                isDisabled={isStartDateDisabled}
                isRequired={!isGiftingOnly}
              >
                <FormLabel>Start date{isGiftingOnly ? ' (optional)' : ''}</FormLabel>
                <Controller
                  control={control}
                  name="startDate"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <DatePickerInput
                      placeholder="Enter their start date"
                      defaultValue={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      isDisabled={isStartDateDisabled}
                    />
                  )}
                />
                <FormErrorMessage>{errors?.startDate?.message}</FormErrorMessage>
              </FormControl>

              {!isGiftingOnly && (
                <>
                  <FormControl isInvalid={!!errors?.payCalendarId?.message} isRequired>
                    <FormLabel>Pay calendar</FormLabel>
                    <Controller
                      control={control}
                      name="payCalendarId"
                      render={({ field: { onBlur } }) => (
                        <DropdownInput
                          placeholder="Select pay calendar"
                          options={payCalendarOptions}
                          onChange={(value) =>
                            setValue('payCalendarId', value.toString(), { shouldValidate: true })
                          }
                          onBlur={onBlur}
                          isSearchable
                        />
                      )}
                    />
                    <FormErrorMessage>{errors?.payCalendarId?.message}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors?.externalPayrollId?.message} isRequired>
                    <FormLabel>Payroll ID</FormLabel>
                    <Input
                      type="text"
                      placeholder="Enter their payroll ID"
                      {...register('externalPayrollId')}
                    />
                    <FormErrorMessage>{errors?.externalPayrollId?.message}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors?.salaried?.message} isRequired>
                    <FormLabel>Employment type</FormLabel>
                    <RadioGroup
                      mt="14px"
                      value={getSalariedValue(watchSalaried)}
                      onChange={(value) =>
                        setValue('salaried', value === 'salaried', { shouldValidate: true })
                      }
                      sx={{ '.chakra-radio__label': { fontSize: '14px', fontWeight: '500' } }}
                    >
                      <Stack spacing="10px">
                        <Radio value="salaried">Salaried</Radio>
                        <Radio value="non-salaried">Non-salaried</Radio>
                      </Stack>
                    </RadioGroup>
                    <FormErrorMessage>{errors?.salaried?.message}</FormErrorMessage>
                  </FormControl>
                </>
              )}
            </>
          )}

          {isAdminRole &&
            renderAdminFields(
              watchCanTopUpCompanyPoints,
              user,
              setValue,
              isEditMode,
              control,
              errors,
              isOnlyAdminWithPointsPermission
            )}

          <HStack spacing="20px" pt="20px">
            <Text color="system.error" textStyle="extraSmallMedium">
              * mandatory
            </Text>
            <Spacer />
            <Button
              variant="link"
              size="sm"
              onClick={onCancel}
              isDisabled={isSubmitting || isLoading}
            >
              Cancel
            </Button>
            <Button
              form="person-form"
              type="submit"
              size="sm"
              isDisabled={isSubmitDisabled}
              isLoading={isSubmitting || isLoading}
            >
              {submitButtonText}
            </Button>
          </HStack>
        </Stack>
      </form>
    </Flex>
  );
}

export default PersonForm;
